import TickerTimeout from "../../utils/TickerTimeout";
import SpineAnimationData from "../../utils/SpineAnimationData";

/**
 * Slack on top of the longest landing clip, for the stall guard below.
 *
 * Two frames at 60fps — enough that a bounce finishing on its very last frame
 * is never beaten to it by the guard, and small enough that a spine which has
 * genuinely died does not hold the round.
 */
const LANDING_GUARD_MARGIN_MS = 34;

/**
 * How long to wait, at the very most, for the board to stop moving.
 *
 * READ FROM THE SPINE DATA, never written down: the longest landing clip in the
 * game plus a margin. A re-export that lengthens a bounce moves this with it.
 *
 * @param {number} fallbackMs Used only before the spine cache exists.
 */
export const landingSettleGuardMs = (fallbackMs) =>
  (SpineAnimationData.getMaxLandingDuration() ?? fallbackMs) +
  LANDING_GUARD_MARGIN_MS;

/**
 * Resolve once every landing animation on the board has finished.
 *
 * ── Why anything waits at all ──
 *
 * A landing bounce is 367-667ms and it plays on TRACK 0. Both of the things
 * that follow a spin take that same track: the win presentation sets its win
 * clip on it, and the next autoplay spin clears it outright. Either one
 * arriving early cuts the bounce off — always on the LAST reel, because the
 * reels stop 200ms apart and only the final one is still moving when a fixed
 * delay expires. Reported 2026-09-10, on both paths.
 *
 * Fixed delays cannot express this: 250ms was shorter than every landing clip
 * in the game, and in turbo — where nothing lands at all — it was 250ms of
 * waiting for nothing.
 *
 * ── What it waits on ──
 *
 * Each overlay reel registers its own landing clips and reports when they are
 * done (OverlayReelController.waitForLandings). A cell with no landing clip
 * never registers, and a reel with none resolves instantly — so a board that
 * animates nothing, or a turbo spin with landings switched off, costs nothing.
 *
 * ── The guard is not optional ──
 *
 * A pooled spine can have `autoUpdate` switched off mid-clip and then never
 * fire `complete`. Without a ceiling that is a round that never pays and an
 * autoplay that never continues. The ceiling pays out regardless.
 *
 * @param   {Array}    controllers Overlay reel controllers; nulls are skipped.
 * @param   {object}   [opts]
 * @param   {number}   [opts.fallbackMs] Guard fallback before the spine cache
 *          is built.
 * @param   {Function} [opts.diag] Called with (via, guardMs) when it settles —
 *          `landings` is the healthy path, `stall guard` means something never
 *          reported and is worth chasing.
 * @returns {Promise<void>} Never rejects.
 */
export const waitForBoardToSettle = (controllers, opts = {}) => {
  const { fallbackMs = 250, diag = null } = opts;
  const landings = (controllers || [])
    .filter(Boolean)
    .map((c) => c.waitForLandings?.() ?? Promise.resolve());

  if (!landings.length) return Promise.resolve();

  const guardMs = landingSettleGuardMs(fallbackMs);

  return new Promise((resolve) => {
    let settled = false;
    let guard = null;
    const finish = (via) => {
      if (settled) return;
      settled = true;
      guard?.clear?.();
      diag?.(via, guardMs);
      resolve();
    };
    guard = new TickerTimeout(() => finish("stall guard"), guardMs);
    Promise.all(landings).then(() => finish("landings"));
  });
};

export default waitForBoardToSettle;
